<script lang="ts" setup>
import bus from '@/helper/Bus'
import { ModalTypes } from '@/helper/ModalTypes'
import ModalComponent from '@/components/ModalComponent.vue'
import userStore from '@/store/UserStore'
import { BusEvent, InstallationType, ModalResultType, StringMap } from '@/helper/Types'
import { validateComponent, ValidatorType } from '@/helper/ValidateDirective'
import FormElement from '@/components/FormElement.vue'
import ModalResult from '@/services/modals/ModalResult'
import { onUnmounted, reactive, ref } from 'vue'
import { modalService } from '@/services/modals/ModalService'
import { formatPrice } from '@/helper/Translations'
import { ProductResponse } from '@/services/models/ProductResponse'
import ProjectController from '@/controllers/ProjectController'
import projectStore from '@/store/ProjectStore.ts'
import MissingAccessoriesModal from '@/components/modals/MissingAccessoriesModal.vue'
import SchemeDebugInfoModal from '@/components/modals/SchemeDebugInfoModal.vue'
import PsUploadModal from '@/components/modals/PsUploadModal.vue'
import PsUpdateModal from '@/components/modals/PsUpdateModal.vue'
import SimulationSuccessModal from '@/components/modals/SimulationSuccessModal.vue'

const validator: ValidatorType = reactive({})
const COMPONENT_NAME = 'ModalHandler'

const resolveModal = ref<(modalResult: ModalResult<unknown>) => void>()
const visibleModal = ref<ModalTypes | undefined>('' as ModalTypes)
const payload = ref(undefined)
const iconPath = '/static/img/modal-icons.svg'
const events: { [key: string]: BusEvent } = {
  confirmEventName: BusEvent.NO_OP,
  cancelEventName: BusEvent.NO_OP,
  closeEventName: BusEvent.NO_OP
}

bus.$onC(COMPONENT_NAME, BusEvent.SHOW_MODAL, showModal)
bus.$onC(COMPONENT_NAME, BusEvent.CLOSE_MODAL, closeModal)

modalService.registerModalComponent(showModal)

function modalIsVisible(type: string): boolean {
  return visibleModal.value === (type as ModalTypes)
}

function getProductTitle(item: ProductResponse): string {
  return ProjectController.getProductTitle(item)
}

function confirmClicked(modalPayload: any = payload.value, modalParams = null) {
  if (!validateComponent(validator)) {
    return
  }

  closeModal()
  resolveModal.value?.(new ModalResult<unknown>(ModalResultType.CONFIRM, payload.value, modalParams))

  if (events.confirmEventName) {
    bus.$emit(events.confirmEventName, modalPayload, modalParams)
  }
}

function cancelClicked(): void {
  closeModal()
  resolveModal.value?.(new ModalResult<unknown>(ModalResultType.CANCEL, payload.value))
  if (events.cancelEventName) {
    bus.$emit(events.cancelEventName, payload.value)
  }
}

function closeClicked(): void {
  closeModal()
  resolveModal.value?.(new ModalResult<unknown>(ModalResultType.CLOSE, payload.value))
  if (events.closeEventName) {
    bus.$emit(events.closeEventName, payload.value)
  }
}

function showModal(modalParams: StringMap): Promise<ModalResult<unknown>> {
  for (const event of Object.keys(events)) {
    events[event] = event in modalParams ? modalParams[event] : ''
  }

  payload.value = modalParams.payload || {}
  visibleModal.value = modalParams.modalType as ModalTypes

  document.body.classList.add('modal-open')

  return new Promise(resolve => {
    resolveModal.value = resolve
  })
}

function closeModal(): void {
  document.body.classList.remove('modal-open')
  visibleModal.value = undefined
}

onUnmounted((): void => {
  bus.$offC(COMPONENT_NAME)
})

defineExpose({
  validator
})
</script>

<template>
  <div class="modal-wrapper">
    <transition name="modal-fade">
      <ModalComponent v-if="modalIsVisible('SIMULATION')" :has-controls="false" class="is--status">
        <template #title>
          {{ $t('modal.simulation_title') }}
        </template>
        <template #text>
          <div class="modal__icon">
            <img alt="loading-icon" class="is--loading loading-icon" src="/static/img/sync2.svg">
          </div>
          <span v-sanitize-html="$t('modal.simulation_text')" />
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <SimulationSuccessModal
        v-if="modalIsVisible(ModalTypes.SIMULATION_SUCCESS)"
        @confirm-clicked="confirmClicked"
        @cancel-clicked="cancelClicked"
        @close-clicked="closeClicked"
      />
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('SIMULATION_ERROR')"
        :has-controls="true"
        :is-closable="true"
        class="is--status is--error"
        @cancel="cancelClicked"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.simulation_title') }}
        </template>
        <template #text>
          <div class="modal__icon">
            <i class="ic-times" />
          </div>
          <span v-sanitize-html="$t('modal.simulation_error_text')" />
        </template>
        <template #confirm>
          {{ $t('overview.btn_create') }}
        </template>
        <template #cancel>
          {{ $t('global.btn_back_to_overview') }}
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('DOWNLOAD_SIMULATION_FAILED')"
        :has-controls="false"
        :is-closable="true"
        class="is--status is--error"
        @cancel="cancelClicked"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.simulation_title') }}
        </template>
        <template #text>
          <div class="modal__icon">
            <i class="ic-times" />
          </div>
          <span v-sanitize-html="$t('modal.download_simulation_failed_text')" />
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('UNSAVED_CHANGES')"
        :has-controls="true"
        :is-closable="true"
        :no-primary-btns="true"
        @cancel="cancelClicked"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.unsaved_title') }}
        </template>
        <template #text>
          <span v-sanitize-html="$t('modal.unsaved_text')" />
        </template>
        <template #confirm>
          {{ $t('modal.unsaved_continue') }}
        </template>
        <template #cancel>
          {{ $t('modal.unsaved_cancel') }}
        </template>
      </ModalComponent>
    </transition>
    <PsUploadModal
      v-if="modalIsVisible(ModalTypes.PS_UPLOAD_SUCCESS)|| modalIsVisible(ModalTypes.PS_UPLOAD_FAILED)"
      :modal-type="visibleModal"
      @confirm-clicked="confirmClicked"
      @cancel-clicked="cancelClicked"
      @close-clicked="closeClicked"
    />
    <PsUpdateModal
      v-if="modalIsVisible(ModalTypes.PS_UPDATE_PROJECT)"
      :payload="payload"
      @confirm-clicked="confirmClicked"
      @cancel-clicked="cancelClicked"
      @close-clicked="closeClicked" />
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('DELETE')"
        :has-controls="true"
        @cancel="cancelClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('overview.delete_question') }}
        </template>
        <template #confirm>
          {{ $t('overview.delete_button') }}
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('COPY')"
        :has-controls="true"
        class="is--copy"
        @cancel="cancelClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.copy_title') }}
        </template>
        <template #text>
          <div class="form__element">
            <FormElement
              v-validate="'required'"
              :error="validator.projectName?.error"
              :required="true"
              label-key="modal.copy_project_name"
            >
              <input
                id="project-name"
                v-model="payload.copiedName"
                :placeholder="$t('step_details.placeholder_project_name')"
                name="projectName"
                type="text"
              >
            </FormElement>
          </div>
        </template>
        <template #confirm>
          {{ $t('modal.copy_confirm_btn_text') }}
        </template>
      </ModalComponent>
    </transition>

    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('SCHEME')"
        :has-controls="true"
        :is-closable="true"
        class="is--scheme"
        @cancel="cancelClicked"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.scheme_title') }}
        </template>
        <template #text>
          <p v-sanitize-html="$t('modal.scheme_text')" />
          <FormElement
            v-validate="'required|email'"
            :error="validator.email?.error"
            :required="true"
            label-key="modal.scheme_input_label"
          >
            <input
              id="email"
              v-model="payload.mail"
              :placeholder="$t('step_details.placeholder_email')"
              name="email"
              type="email"
            >
          </FormElement>
          <label class="form__checkbox">
            <input id="cb-email-cop" v-model="payload.sendCopy" type="checkbox">
            <span class="form__cm" />
            {{ $t('modal.scheme_checkbox_label') }}
          </label>
        </template>
        <template #confirm>
          {{ $t('modal.scheme_confirm_btn_text') }}
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('SCHEME_SUCCESS')"
        :has-controls="true"
        :has-no-cancel="true"
        :is-closable="true"
        class="is--scheme-success is--large"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.scheme_success_title') }}
        </template>
        <template #text>
          <div class="modal__icon">
            <i class="ic-check" />
          </div>
          <span>{{ $t('modal.scheme_success_text') }}</span>
        </template>
        <template #confirm>
          {{ $t('modal.scheme_success_confirm_btn_text') }}
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('PLACEMENT_INFO')"
        :has-controls="true"
        :has-no-cancel="true"
        :is-closable="true"
        class="is--info placement-info"
        @cancel="cancelClicked"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.placement_info_title') }}
        </template>
        <template #text>
          <div class="info__intro">
            <p>{{ $t('modal.placement_info_intro') }}</p>
          </div>
          <div class="info__content">
            <div class="row no-gutters">
              <div class="col-md-6 info__image">
                <svg viewBox="0 0 444 360">
                  <use :href="`${iconPath}#illustration-no-walls-` + userStore.themeClass" />
                </svg>
              </div>
              <div class="col-md-6 info__description">
                <span class="info__title h2">{{ $t('modal.placement_info_free_title') }}</span>
                <ul class="check-list is--green info__check-list">
                  <li>{{ $t('modal.placement_info_free_bullet1') }}</li>
                  <li>{{ $t('modal.placement_info_free_bullet2') }}</li>
                  <li>{{ $t('modal.placement_info_free_bullet3') }}</li>
                </ul>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-md-6 info__description">
                <span class="info__title h2">{{ $t('modal.placement_info_one_wall_title') }}</span>
                <ul class="check-list is--green info__check-list">
                  <li>{{ $t('modal.placement_info_one_wall_bullet1') }}</li>
                  <li>{{ $t('modal.placement_info_one_wall_bullet2') }}</li>
                  <li>{{ $t('modal.placement_info_one_wall_bullet3') }}</li>
                </ul>
              </div>
              <div class="col-md-6 info__image">
                <svg viewBox="0 0 444 360">
                  <use :href="`${iconPath}#illustration-one-wall-` + userStore.themeClass" />
                </svg>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-md-6 info__image">
                <svg viewBox="0 0 444 360">
                  <use :href="`${iconPath}#illustration-two-walls-` + userStore.themeClass" />
                </svg>
              </div>
              <div class="col-md-6 info__description">
                <span class="info__title h2">{{ $t('modal.placement_info_two_walls_title') }}</span>
                <ul class="check-list is--green info__check-list">
                  <li>{{ $t('modal.placement_info_two_walls_bullet1') }}</li>
                  <li>{{ $t('modal.placement_info_two_walls_bullet2') }}</li>
                  <li>{{ $t('modal.placement_info_two_walls_bullet3') }}</li>
                </ul>
              </div>
            </div>
          </div>
        </template>
        <template #confirm>
          {{ $t('modal.placement_info_btn_ok') }}
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('HEATING_CALC_INFO')"
        :has-controls="true"
        :has-no-cancel="true"
        :is-closable="true"
        class="is--info heating-calculation-info"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.heating_calculation_info_title') }}
        </template>
        <template #text>
          <div class="info__content">
            <div class="row no-gutters">
              <div class="col-md-6 info__image">
                <svg viewBox="0 0 444 360">
                  <use :href="`${iconPath}#illustration-house-condition-` + userStore.themeClass" />
                </svg>
              </div>
              <div class="col-md-6 info__description">
                <span class="info__title h2">{{ $t('modal.heating_calculation_info_condition_title') }}</span>
                <span class="info__text">{{ $t('modal.heating_calculation_info_condition_text') }}</span>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-md-6 info__description">
                <span class="info__title h2">{{ $t('modal.heating_calculation_info_height_title') }}</span>
                <span class="info__text">{{ $t('modal.heating_calculation_info_height_text') }}</span>
                <ul class="check-list is--green info__check-list">
                  <li>{{ $t('modal.heating_calculation_info_height_bullet1') }}</li>
                  <li>{{ $t('modal.heating_calculation_info_height_bullet2') }}</li>
                  <li>{{ $t('modal.heating_calculation_info_height_bullet3') }}</li>
                </ul>
              </div>
              <div class="col-md-6 info__image is--large">
                <svg viewBox="0 0 444 360">
                  <use :href="`${iconPath}#illustration-house-height-` + userStore.themeClass" />
                </svg>
              </div>
            </div>
          </div>
        </template>
        <template #confirm>
          {{ $t('modal.placement_info_btn_ok') }}
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('COOLING_CALC_INFO')"
        :has-controls="true"
        :has-no-cancel="true"
        :is-closable="true"
        class="is--info cooling-calculation-info"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.cooling_calculation_info_title') }}
        </template>
        <template #text>
          <div class="info__intro">
            <p>{{ $t('modal.placement_info_intro') }}</p>
          </div>
          <div class="info__content">
            <div class="row no-gutters">
              <div class="col-md-6 info__image">
                <svg viewBox="0 0 444 360">
                  <use :href="`${iconPath}#illustration-house-condition-` + userStore.themeClass" />
                </svg>
              </div>
              <div class="col-md-6 info__description">
                <span class="info__title h2">{{ $t('modal.cooling_calculation_info_condition_title') }}</span>
                <span class="info__text">{{ $t('modal.cooling_calculation_info_condition_text') }}</span>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-md-6 info__description">
                <span class="info__title h2">{{ $t('modal.cooling_calculation_info_height_title') }}</span>
                <span class="info__text">{{ $t('modal.cooling_calculation_info_height_text') }}</span>
                <ul class="check-list is--green info__check-list">
                  <li>{{ $t('modal.cooling_calculation_info_height_bullet1') }}</li>
                  <li>{{ $t('modal.cooling_calculation_info_height_bullet2') }}</li>
                  <li>{{ $t('modal.cooling_calculation_info_height_bullet3') }}</li>
                </ul>
              </div>
              <div class="col-md-6 info__image is--large">
                <svg viewBox="0 0 444 360">
                  <use :href="`${iconPath}#illustration-house-height-` + userStore.themeClass" />
                </svg>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-md-6 info__image">
                <svg viewBox="0 0 444 360">
                  <use :href="`${iconPath}#illustration-house-windows-` + userStore.themeClass" />
                </svg>
              </div>
              <div class="col-md-6 info__description">
                <span class="info__title h2">{{ $t('modal.cooling_calculation_info_windows_title') }}</span>
                <span class="info__text">{{ $t('modal.cooling_calculation_info_windows_text') }}</span>
                <ul class="info__list check-list info__check-list">
                  <li>{{ $t('modal.cooling_calculation_info_windows_bullet1') }}</li>
                  <li>{{ $t('modal.cooling_calculation_info_windows_bullet2') }}</li>
                  <li>{{ $t('modal.cooling_calculation_info_windows_bullet3') }}</li>
                </ul>
              </div>
            </div>
          </div>
        </template>
        <template #confirm>
          {{ $t('modal.placement_info_btn_ok') }}
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('PRODUCT_DETAILS')"
        :has-controls="true"
        :is-closable="true"
        class="is--product-details"
        @cancel="cancelClicked"
        @close="closeClicked"
        @confirm="confirmClicked(null, payload.id)"
      >
        <template #title>
          {{ payload.name }}
        </template>
        <template #text>
          <div v-if="payload.recommended" class="top-badge">
            {{ $t('step_heat_pumps.recommended_badge') }}
          </div>
          <div class="modal__product-image">
            <div class="image-box is--zoomed has--brand-bg">
              <div :class="!payload.pictureUrl && 'fallback-bg'" class="image-box__container">
                <img v-if="payload.pictureUrl" :alt="payload.name" :src="payload.pictureUrl" loading="lazy">
              </div>
            </div>
          </div>
          <ul class="check-list is--green">
            <li v-for="(item, i) in payload.productAdvantages" :key="i">
              {{ item }}
            </li>
          </ul>
        </template>
        <template #confirm>
          {{ $t('modal.additional_product_select') }}
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('LOGIN_FAILURE')"
        :has-controls="false"
        :is-closable="false"
        :no-primary-btns="true"
      >
        <template #title>
          {{ $t('global.login_fail_title') }}
        </template>
        <template #text>
          <span v-sanitize-html="$t('global.login_fail_text')" />
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('CONFIG_DELETION_WARNING')"
        :has-controls="true"
        :is-closable="false"
        class="is--status is--error"
        @cancel="cancelClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          Deletion warning
        </template>
        <template #text>
          <div class="modal__icon">
            <i class="ic-">!</i>
          </div>
          <span>You are about to delete one or multiple entries. This may influence existing projects and potentially make
            them invalid. When in doubt please contact the developing team. <br><br>Are you sure you want to
            proceed?
          </span>
        </template>
        <template #confirm>
          Delete
        </template>
        <template #cancel>
          Cancel
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('CONFIG_DISABLING_WARNING')"
        :has-controls="true"
        :is-closable="false"
        class="is--status is--error"
        @cancel="cancelClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          Disabling warning
        </template>
        <template #text>
          <div class="modal__icon">
            <i class="ic-">!</i>
          </div>
          <span>You are about to disable a feature. This may influence existing projects and potentially make them invalid.
            When in doubt please contact the developing team. <br><br>Are you sure you want to proceed?
          </span>
        </template>
        <template #confirm>
          Disable
        </template>
        <template #cancel>
          Cancel
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('CONFIG_KEY_CHANGE_WARNING')"
        :has-controls="true"
        :is-closable="false"
        class="is--status is--error"
        @cancel="cancelClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          Key change warning
        </template>
        <template #text>
          <div class="modal__icon">
            <i class="ic-">!</i>
          </div>
          <span>You are about to change a key. This may influence existing projects and potentially make them invalid.
            Remember to set the translation key accordingly before changing the key. When in doubt please contact the
            developing team. <br><br>Are you sure you want to proceed?
          </span>
        </template>
        <template #confirm>
          Change key
        </template>
        <template #cancel>
          Cancel
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('HELP')"
        :has-controls="false"
        :has-no-cancel="true"
        :is-closable="true"
        @cancel="cancelClicked"
        @close="closeClicked"
        @confirm="confirmClicked(payload)"
      >
        <template #title>
          {{ payload.modalTitle }}
        </template>
        <template v-if="payload.modalText" #text>
          <!-- eslint-disable vue/no-v-html -->
          <div v-html="payload.modalText" />
          <!--eslint-enable-->
        </template>
        <template v-if="payload.confirmButtonText" #confirm>
          {{ payload.confirmButtonText }}
        </template>
        <template v-if="payload.cancelButtonText" #cancel>
          {{ payload.cancelButtonText }}
        </template>
      </ModalComponent>
    </transition>
    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('GENERAL')"
        :has-controls="true"
        @cancel="cancelClicked"
        @confirm="confirmClicked(payload)"
      >
        <template #title>
          {{ payload.modalTitle }}
        </template>
        <template v-if="payload.modalText" #text>
          <span>{{ payload.modalText }}</span>
        </template>
        <template v-if="payload.confirmButtonText" #confirm>
          {{ payload.confirmButtonText }}
        </template>
        <template v-if="payload.cancelButtonText" #cancel>
          {{ payload.cancelButtonText }}
        </template>
      </ModalComponent>
    </transition>

    <transition name="modal-fade">
      <MissingAccessoriesModal
        v-if="modalIsVisible(ModalTypes.ACCESSORY_SUGGESTION)"
        :missing-accesories="payload.articles"
        @confirm-clicked="confirmClicked"
        @cancel-clicked="cancelClicked"
        @close-clicked="closeClicked"
      />
    </transition>

    <transition name="modal-fade">
      <SchemeDebugInfoModal
        v-if="modalIsVisible(ModalTypes.SCHEME_DEBUG_INFO)"
        :scheme="payload.scheme"
        @close-clicked="closeClicked"
      />
    </transition>

    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('UPDATE_PROJECT')"
        :confirm-disabled="!payload.updateConfirmed"
        :has-controls="true"
        :is-closable="true"
        class="is--update"
        @cancel="cancelClicked"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title />
        <template #text>
          <div v-if="payload.updateProducts.phasedOut.length" class="modal__section-header">
            <div class="h2 modal__subtitle">
              {{ $t('modal.phased_out_title') }}
            </div>
            <div class="modal__update-description span">
              {{ $t('modal.phased_out_description') }}:
            </div>
          </div>
          <div class="modal__update-products-list">
            <div
              v-for="(product, i) in payload.updateProducts.phasedOut"
              :key="'update-product-' + i"
              :data-id="product.id"
              class="modal__update-product"
            >
              <div class="modal__update-image">
                <img v-if="product.pictureUrl" :alt="product.name" :src="product.pictureUrl">
              </div>
              <div class="modal__update-data">
                <div class="span">
                  {{ getProductTitle(product) }}
                </div>
                <div class="span">
                  {{ product.name }}
                </div>
                <div class="span">
                  {{ $t('step_accessories.table_order_number') }}:
                  {{ product.orderNumber }}
                </div>
              </div>
            </div>
          </div>
          <div v-if="payload.updateProducts.priceUpdate.length" class="modal__section-header">
            <div class="h2 modal__subtitle">
              {{ $t('modal.update_price_title') }}
            </div>
            <div class="modal__update-description span">
              {{ $t('modal.update_price_description') }}:
            </div>
          </div>
          <div class="modal__update-products-list">
            <div
              v-for="(product, i) in payload.updateProducts.priceUpdate"
              :key="'update-product-' + i"
              :data-id="product.id"
              class="modal__update-product"
            >
              <div class="modal__update-image">
                <img v-if="product.pictureUrl" :alt="product.name" :src="product.pictureUrl">
              </div>
              <div class="modal__update-data">
                <div class="span">
                  {{ getProductTitle(product) }}
                </div>
                <div class="span">
                  {{ product.name }}
                </div>
                <div class="span">
                  {{ $t('step_accessories.table_order_number') }}:
                  {{ product.orderNumber }}
                </div>
              </div>
              <div class="modal__update-status">
                <div class="modal__old-price span">
                  {{ $t('modal.old_price') }}:
                  {{ formatPrice(product.price, userStore.currentIsoLocale) }}
                </div>
                <div v-if="payload.updateProducts.customPrices[product.id] !== undefined" class="modal__old-price span">
                  {{ $t('modal.custom_price') }}:
                  {{ formatPrice(payload.updateProducts.customPrices[product.id], userStore.currentIsoLocale) }}
                </div>
                <div class="modal__new-price span">
                  {{ $t('modal.new_price') }}:
                  {{ formatPrice(product.updatedPrice, userStore.currentIsoLocale) }}
                </div>
              </div>
            </div>
          </div>

          <div class="modal__update-warning">
            <div class="modal__update-warning-icon">
              <i class="ic-alert" />
            </div>
            <div class="modal__update-warning-text">
              <div class="span">
                {{ $t('modal.update_warning1') }}<br>
                <b>{{ $t('modal.update_warning2') }}:</b>
                {{ $t('modal.update_warning3') }}
                <span v-if="payload.updateProducts.priceUpdate.length" class="span">
                  {{ $t('modal.update_custom_price') }}
                </span>
              </div>
              <FormElement>
                <label class="form__checkbox">
                  <input id="confirm-update-cb" v-model="payload.updateConfirmed" type="checkbox">
                  <span class="form__cm" />
                  {{ $t('modal.update_warning4') }}
                </label>
              </FormElement>
            </div>
          </div>
        </template>
        <template #confirm>
          {{ $t('modal.btn_update_project') }}
        </template>
        <template #cancel>
          {{ $t('modal.btn_cancel') }}
        </template>
      </ModalComponent>
    </transition>

    <transition name="modal-fade">
      <ModalComponent
        v-if="modalIsVisible('BUFFER_VOLUME_INFO')"
        :has-controls="true"
        :has-no-cancel="true"
        :is-closable="true"
        @cancel="cancelClicked"
        @close="closeClicked"
        @confirm="confirmClicked"
      >
        <template #title>
          {{ $t('modal.buffer_volume.headline') }}
        </template>
        <template #text>
          <div class="info__intro">
            <p>{{ $t('modal.buffer_volume.description') }}</p>
          </div>
          <div class="info__content">
            <div class="info__table">
              <div class="info__table-header">
                <span>{{ $t('modal.buffer_volume.operation_mode') }}</span>
                <span>{{ $t('modal.buffer_volume.necessary_volume') }}</span>
              </div>
              <div
                v-if="(!payload?.installationType || payload?.installationType === InstallationType.HEATING) && projectStore.bufferTankSuggestion?.minVolume?.heating">
                <span>{{ $t('modal.buffer_volume.heating') }}</span>
                <span>{{ Math.round(projectStore.bufferTankSuggestion?.minVolume?.heating || 0) }} l</span>
              </div>
              <div
                v-if="(!payload?.installationType || payload?.installationType === InstallationType.COOLING) && projectStore.bufferTankSuggestion?.minVolume?.cooling">
                <span>{{ $t('modal.buffer_volume.cooling') }}</span>
                <span>{{ Math.round(projectStore.bufferTankSuggestion?.minVolume?.cooling || 0) }} l</span>
              </div>
              <div v-if="projectStore.bufferTankSuggestion?.minVolume?.defrosting">
                <span>{{ $t('modal.buffer_volume.defrosting') }}</span>
                <span>{{ Math.round(projectStore.bufferTankSuggestion?.minVolume?.defrosting || 0) }} l</span>
              </div>
              <div
                v-if="
                  projectStore.bufferTankSuggestion?.minVolume?.combined
                    || projectStore.bufferTankSuggestion?.minVolume?.heatingEffective
                    || projectStore.bufferTankSuggestion?.minVolume?.coolingEffective"
                class="info__table-footer">
                <span>{{ $t('modal.buffer_volume.effectively_volume') }}</span>
                <span v-if="!payload?.installationType">
                  {{
                    Math.round(
                      projectStore.bufferTankSuggestion?.minVolume?.combined
                        || projectStore.bufferTankSuggestion?.minVolume?.heatingEffective
                        || projectStore.bufferTankSuggestion?.minVolume?.coolingEffective
                        || 0
                    )
                  }} l
                </span>
                <span v-if="payload?.installationType === InstallationType.HEATING">
                  {{ Math.round(projectStore.bufferTankSuggestion?.minVolume?.heatingEffective || 0) }} l
                </span>
                <span v-if="payload?.installationType === InstallationType.COOLING">
                  {{ Math.round(projectStore.bufferTankSuggestion?.minVolume?.coolingEffective || 0) }} l
                </span>
              </div>
            </div>
          </div>
        </template>
        <template #confirm>
          {{ $t('modal.placement_info_btn_ok') }}
        </template>
      </ModalComponent>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.svg-sprite {
  visibility: hidden;
  position: absolute;
  width: 0;
  height: 0;
}

.modal {
  z-index: 20;

  .info {
    &__check-list {
      margin-top: rem-calc(22);
    }

    .row {
      min-height: rem-calc(300);
    }

    &__table {
      border: 1px solid $borderColorLight;
      border-radius: rem-calc(5);

      &-header,
      &-footer {
        font-weight: bold;

        span {
          &:first-child {
            text-align: left;
          }

          &:last-child {
            text-align: right;
          }
        }
      }

      div {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid $borderColorLight;
        padding: 1rem;

        &:last-child {
          border-bottom: none;
        }
      }
    }

    &__content {
      margin-top: rem-calc(56);
    }

    &__description {
      padding: rem-calc(32);
    }

    &__intro {
      padding-right: rem-calc(32);
      padding-left: rem-calc(32);
      margin-bottom: 0;
    }

    &__title {
      display: block;

      @include themify($themes) {
        color: $textColor;
        margin-bottom: rem-calc(24);
      }
    }

    &__text {
      display: block;
      margin-top: rem-calc(14);
    }

    &__image {
      display: flex;
      align-items: center;
      justify-content: center;
      background: $bgColor;

      svg {
        height: rem-calc(215);
      }

      &.is--large {
        svg {
          width: rem-calc(338);
          height: auto;
        }
      }
    }
  }
}
</style>
